import Box from '@mui/material/Box';
import {urlPrefixed} from '@wandb/weave/config';
import {useViewTraceEvent} from '@wandb/weave/integrations/analytics/useViewEvents';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {makeRefCall} from '../../../../../../util/refs';
import {Button} from '../../../../../Button';
import {ErrorBoundary} from '../../../../../ErrorBoundary';
import {Tailwind} from '../../../../../Tailwind';
import {TableRowSelectionContext} from '../../../TableRowSelectionContext';
import {TraceNavigator} from '../../components/TraceNavigator/TraceNavigator';
import {useBackNavigation, WeaveflowPeekContext} from '../../context';
import {FeedbackGrid} from '../../feedback/FeedbackGrid';
import {Reactions} from '../../feedback/Reactions';
import {ScorerFeedbackGrid} from '../../feedback/ScorerFeedbackGrid';
import {FeedbackSidebar} from '../../feedback/StructuredFeedback/FeedbackSidebar';
import {useHumanAnnotationSpecs} from '../../feedback/StructuredFeedback/tsHumanFeedback';
import {NotFoundPanel} from '../../NotFoundPanel';
import {OpDefCode} from '../../OpDefCode';
import {isCallChat} from '../ChatView/hooks';
import {EditableCallName} from '../common/EditableCallName';
import {isEvaluateOp} from '../common/heuristics';
import {CenteredAnimatedLoader} from '../common/Loader';
import {
  ScrollableTabContent,
  SimplePageLayoutWithHeader,
} from '../common/SimplePageLayout';
import {StatusChip} from '../common/StatusChip';
import {CompareEvaluationsPageContent} from '../CompareEvaluationsPage/CompareEvaluationsPage';
import {DocumentView} from '../DocumentView/DocumentView';
import {callHasDocuments, parseCall} from '../DocumentView/parser';
import {useWFHooks} from '../wfReactInterface/context';
import {useAutoRefresh} from '../wfReactInterface/RefreshContext';
import {ComputedCallStatuses} from '../wfReactInterface/traceServerClientTypes';
import {traceCallStatusCode} from '../wfReactInterface/tsDataModelHooks';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';
import {CallChat} from './CallChat';
import {CallDetails} from './CallDetails';
import {CallErrorMessage} from './CallErrorMessage';
import {CallLogs} from './CallLogs';
import {CallPagePaginationControls} from './CallPagePaginationControls';
import {CallSummary} from './CallSummary';
import {CallViewEntry, CallViews} from './CallViews';
import {OverflowMenu} from './OverflowMenu';
import {TabUseCall} from './TabUseCall';

type CallPageProps = {
  entity: string;
  project: string;
  rootCallId: string;
  setRootCallId: (callId: string) => void;
  focusedCallId?: string;
  setFocusedCallId: (focusedCallId: string | undefined) => void;
  hideTraceTree?: boolean;
  setHideTraceTree: (hideTraceTree: boolean | undefined) => void;
  showFeedback?: boolean;
  setShowFeedback: (showFeedback: boolean | undefined) => void;
  traceSearchQuery: string;
  setTraceSearchQuery: (query: string) => void;
  traceSearchStrict: boolean;
  setTraceSearchStrict: (strict: boolean) => void;
};

type CallPageInnerProps = CallPageProps & {
  focusedCallId: string;
  setFocusedCallId: (focusedCallId: string) => void;
  focusedCall: CallSchema;
  callIsStale: boolean;
};

export const CallPage: FC<CallPageProps> = props => {
  const {useCall} = useWFHooks();

  const descendentCallId = props.focusedCallId ?? props.rootCallId;

  // Note to future devs: We could delay the cost (and i/o) fetching. This is
  // just needed to validate that the call truly exists.
  const call = useCall({
    key: {
      entity: props.entity,
      project: props.project,
      callId: descendentCallId,
    },
    // Sadly we cannot include costs as unfinished calls will result
    // in null response (bug on server side). As a result, the summary
    // will not show costs. FIXME (This results in a second query in
    // CallSummary.tsx)
    // This flag and the below storage flag can cause long query times
    // defer loading to the summary component
    // includeCosts: true,
    // includeTotalStorageSize: true,
    refetchOnRename: true,
  });

  useAutoRefresh(call.refetch);

  // This is a little hack, but acceptable for now.
  // We don't want the entire page to re-render when the call result is updated.
  const lastResult = useRef(call.result);
  useEffect(() => {
    if (call.result) {
      lastResult.current = call.result;
    }
  }, [call.result]);

  if (!call.loading) {
    if (call.result === null) {
      return <NotFoundPanel title="Call not found" />;
    } else {
      return (
        <CallPageInnerVertical
          {...props}
          focusedCallId={descendentCallId}
          focusedCall={call.result}
          callIsStale={call.result.callId !== descendentCallId}
        />
      );
    }
  } else {
    if (lastResult.current === null) {
      return <CenteredAnimatedLoader />;
    } else {
      return (
        <CallPageInnerVertical
          {...props}
          focusedCallId={descendentCallId}
          focusedCall={lastResult.current}
          callIsStale={lastResult.current.callId !== descendentCallId}
        />
      );
    }
  }
};

const extractCallViews = (call: CallSchema): CallViewEntry[] => {
  const summaryViews = call.traceCall.summary?.weave?.views;
  if (summaryViews && typeof summaryViews === 'object') {
    return Object.entries(summaryViews).map(([name, data]) => ({name, data}));
  }
  return [];
};

const useCallTabs = (call: CallSchema) => {
  const codeURI = call.opVersionRef;
  const {entity, project, callId} = call;
  const weaveRef = makeRefCall(entity, project, callId);

  const [callViews, setCallViews] = useState<CallViewEntry[]>(() =>
    extractCallViews(call)
  );

  useEffect(() => {
    setCallViews(extractCallViews(call));
  }, [call]);

  const {isPeeking} = useContext(WeaveflowPeekContext);
  const showTryInPlayground =
    !isPeeking || !window.location.toString().includes('/weave/playground');

  const handleOpenInPlayground = () => {
    window.open(
      urlPrefixed(`/${entity}/${project}/weave/playground/${callId}`),
      '_blank'
    );
  };

  const callIsEvaluateOp = useMemo(() => {
    return isEvaluateOp(call.spanName);
  }, [call.spanName]);

  const hasWbRunId = Boolean(call.traceCall?.wb_run_id);

  return [
    // Disabling Evaluation tab until it's better for single evaluation
    ...(false && callIsEvaluateOp
      ? [
          {
            label: 'Evaluation',
            content: (
              <CompareEvaluationsPageContent
                entity={call.entity}
                project={call.project}
                evaluationCallIds={[call.callId]}
                // Dont persist metric selection in the URL
                selectedMetrics={{}}
                setSelectedMetrics={() => {}}
                // Dont persist changes to evaluationCallIds in the URL
                onEvaluationCallIdsUpdate={() => {}}
              />
            ),
          },
        ]
      : []),
    ...(call.traceCall && isCallChat(call.traceCall)
      ? [
          {
            label: 'Chat',
            content: (
              <>
                {showTryInPlayground && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%',
                      padding: '8px 16px',
                    }}>
                    <Button
                      variant="ghost"
                      startIcon="video-play"
                      onClick={handleOpenInPlayground}>
                      Try in playground
                    </Button>
                  </Box>
                )}
                <ScrollableTabContent>
                  <Tailwind>
                    <CallChat call={call.traceCall} />
                  </Tailwind>
                </ScrollableTabContent>
              </>
            ),
          },
        ]
      : []),
    ...(call.traceCall && callHasDocuments(call.traceCall)
      ? [
          {
            label: 'Documents',
            content: (
              <ScrollableTabContent>
                <Tailwind>
                  {call.traceCall && <DocumentView data={parseCall(call)} />}
                </Tailwind>
              </ScrollableTabContent>
            ),
          },
        ]
      : []),
    {
      label: 'Call',
      content: <CallDetails call={call} />,
    },
    ...(codeURI && !callIsEvaluateOp
      ? [
          {
            label: 'Code',
            content: <OpDefCode uri={codeURI} />,
          },
        ]
      : []),
    {
      label: 'Feedback',
      content: (
        <Tailwind style={{height: '100%', overflow: 'auto'}}>
          <FeedbackGrid
            entity={entity}
            project={project}
            weaveRef={weaveRef}
            objectType="call"
          />
        </Tailwind>
      ),
    },
    ...(!callIsEvaluateOp
      ? [
          {
            label: 'Scores',
            content: (
              <Tailwind style={{height: '100%', overflow: 'auto'}}>
                <ScorerFeedbackGrid
                  entity={entity}
                  project={project}
                  weaveRef={weaveRef}
                  objectType="call"
                />
              </Tailwind>
            ),
          },
        ]
      : []),
    {
      label: 'Summary',
      content: (
        <Tailwind style={{height: '100%', overflow: 'auto'}}>
          <CallSummary call={call} />
        </Tailwind>
      ),
    },
    // Views tab if any custom views exist
    ...(callViews.length > 0
      ? [
          {
            label: 'Views',
            content: (
              <Tailwind style={{height: '100%', overflow: 'auto'}}>
                <CallViews call={call} views={callViews} />
              </Tailwind>
            ),
          },
        ]
      : []),
    {
      label: 'Use',
      content: (
        <ScrollableTabContent>
          <Tailwind>
            <TabUseCall call={call} />
          </Tailwind>
        </ScrollableTabContent>
      ),
    },
    ...(hasWbRunId
      ? [
          {
            label: 'Logs',
            content: (
              <Tailwind style={{height: '100%', overflow: 'hidden'}}>
                <CallLogs key={call.callId} call={call} />
              </Tailwind>
            ),
          },
        ]
      : []),
  ];
};

const CallPageInnerVertical: FC<CallPageInnerProps> = ({
  focusedCall,
  focusedCallId,
  setRootCallId,
  setFocusedCallId,
  setHideTraceTree,
  setShowFeedback,
  showFeedback,
  hideTraceTree,
  callIsStale,
  rootCallId,
  traceSearchQuery,
  setTraceSearchQuery,
  traceSearchStrict,
  setTraceSearchStrict,
}) => {
  useViewTraceEvent(focusedCall);

  const hasCustomViews =
    ((focusedCall.rawSpan?.summary as any)?.views &&
      Object.keys((focusedCall.rawSpan?.summary as any)?.views ?? {}).length >
        0) ||
    false;
  const hideTraceTreeDefault =
    isEvaluateOp(focusedCall.spanName) || hasCustomViews;
  const showFeedbackDefault = false;
  const hideTraceTreeActual =
    hideTraceTree != null ? hideTraceTree : hideTraceTreeDefault;
  const showFeedbackActual =
    showFeedback != null ? showFeedback : showFeedbackDefault;

  useEffect(() => {
    if (hideTraceTree == null) {
      setHideTraceTree(hideTraceTreeDefault);
    }
  }, [
    hideTraceTree,
    hideTraceTreeActual,
    hideTraceTreeDefault,
    setHideTraceTree,
  ]);

  const onToggleTraceTree = useCallback(() => {
    const targetValue = !hideTraceTreeActual;
    setHideTraceTree(targetValue);
  }, [hideTraceTreeActual, setHideTraceTree]);

  const onToggleFeedbackExpand = useCallback(() => {
    const targetValue = !showFeedbackActual;
    if (targetValue === showFeedbackDefault) {
      setShowFeedback(undefined);
    } else {
      setShowFeedback(targetValue);
    }
  }, [setShowFeedback, showFeedbackDefault, showFeedbackActual]);

  const {humanAnnotationSpecs, specsLoading} = useHumanAnnotationSpecs(
    focusedCall.entity,
    focusedCall.project
  );

  const {rowIdsConfigured} = useContext(TableRowSelectionContext);
  const {isPeeking, previousUrl} = useContext(WeaveflowPeekContext);
  const showPaginationControls = isPeeking && rowIdsConfigured;

  const callTabs = useCallTabs(focusedCall);
  const callTabsWithViewsFirst = useMemo(() => {
    const viewsIndex = callTabs.findIndex(t => t.label === 'Views');
    if (viewsIndex > 0) {
      const viewsTab = callTabs[viewsIndex];
      return [
        viewsTab,
        ...callTabs.slice(0, viewsIndex),
        ...callTabs.slice(viewsIndex + 1),
      ];
    }
    return callTabs;
  }, [callTabs]);
  const backNavigation = useBackNavigation();

  const handleBackClick = useCallback(() => {
    backNavigation();
  }, [backNavigation]);

  const setRootCallIdForPagination = useCallback(
    (callId: string) => {
      setFocusedCallId(callId);
      setRootCallId(callId);
    },
    [setRootCallId, setFocusedCallId]
  );

  const refCall = makeRefCall(
    focusedCall.entity,
    focusedCall.project,
    focusedCall.callId
  );

  const status = focusedCall.traceCall
    ? traceCallStatusCode(focusedCall.traceCall)
    : ComputedCallStatuses.running;

  return (
    <SimplePageLayoutWithHeader
      px={1}
      headerExtra={
        <Tailwind style={{display: 'contents'}}>
          <div className="flex w-full items-center">
            <div className="flex items-center gap-4 mr-4 w-full">
              {isPeeking && previousUrl && (
                <Button
                  icon="back"
                  tooltip="Go back"
                  variant="ghost"
                  onClick={handleBackClick}
                  className="mr-4"
                />
              )}
              {showPaginationControls && (
                <CallPagePaginationControls
                  callId={rootCallId}
                  setRootCallId={setRootCallIdForPagination}
                />
              )}
              <div className="group flex items-center gap-4 w-full">
                <StatusChip value={status} iconOnly />
                <EditableCallName call={focusedCall} />
              </div>
            </div>
            <div className="ml-auto flex gap-4 items-center">
              <Reactions
                size="medium"
                variant="secondary"
                gap={4}
                weaveRef={refCall}
                forceVisible={true}
              />
              <OverflowMenu selectedCalls={[focusedCall]} />
            </div>
          </div>
        </Tailwind>
      }
      leftOfTabs={
        hideTraceTreeActual ? (
          <Tailwind style={{display: 'contents'}}>
            <Button
              icon="layout-tabs"
              size="small"
              tooltip={`${!hideTraceTreeActual ? 'Hide' : 'Show'} calls`}
              variant="ghost"
              onClick={onToggleTraceTree}
              className="mb-6 -ml-4"
            />
          </Tailwind>
        ) : undefined
      }
      rightOfTabs={
        !showFeedbackActual ? (
          <Tailwind style={{display: 'contents'}}>
            <Button
              icon="marker"
              tooltip={`${showFeedbackActual ? 'Hide' : 'Show'} annotation`}
              variant="ghost"
              size="small"
              onClick={onToggleFeedbackExpand}
              className="mb-6 -mr-4"
            />
          </Tailwind>
        ) : undefined
      }
      isRightSidebarOpen={showFeedbackActual}
      rightSidebarContent={
        <Tailwind style={{display: 'contents'}}>
          <div className="flex h-full flex-col">
            <FeedbackSidebar
              humanAnnotationSpecs={humanAnnotationSpecs}
              specsLoading={specsLoading}
              callID={focusedCallId}
              entity={focusedCall.entity}
              project={focusedCall.project}
              onClose={onToggleFeedbackExpand}
            />
          </div>
        </Tailwind>
      }
      {...(focusedCall.rawSpan.exception && {
        headerContent: <CallErrorMessage call={focusedCall} />,
      })}
      isLeftSidebarOpen={!hideTraceTreeActual}
      leftSidebarContent={
        <Tailwind style={{display: 'contents'}}>
          <ErrorBoundary>
            <div className="h-full bg-moon-50">
              <TraceNavigator
                entity={focusedCall.entity}
                project={focusedCall.project}
                traceId={focusedCall.traceId}
                focusedCallId={focusedCallId}
                rootCallId={rootCallId}
                setFocusedCallId={setFocusedCallId}
                setRootCallId={setRootCallId}
                traceSearchQuery={traceSearchQuery}
                setTraceSearchQuery={setTraceSearchQuery}
                traceSearchStrict={traceSearchStrict}
                setTraceSearchStrict={setTraceSearchStrict}
                onClose={onToggleTraceTree}
              />
            </div>
          </ErrorBoundary>
        </Tailwind>
      }
      tabs={callTabsWithViewsFirst}
      dimMainContent={callIsStale}
    />
  );
};
